import { Injectable, inject, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { HttpService } from '../http/http.service';

/** Segmentos de accion/ruta que NO identifican la opcion (se ignoran al derivar la clave). */
const ROUTE_ACTION_SEGMENTS = new Set([
  'list',
  'create',
  'edit',
  'view',
  'detail',
  'new',
  'layout',
]);

/**
 * Ultimo segmento significativo de una ruta (ignora acciones e ids).
 * Ej: '/layout/accounting/tipo-plan/list' -> 'tipo-plan'.
 * Misma logica que SidebarService.routeKeySegment; se replica aqui para no acoplar
 * shared/data-access con layout.
 */
export function routeOptionSegment(route: string | undefined): string {
  if (!route) return '';
  const parts = route
    .split('/')
    .map((p) => p.trim())
    .filter(
      (p) => p && !ROUTE_ACTION_SEGMENTS.has(p) && !/^\d+$/.test(p) && !/^[0-9a-f]{8}-/.test(p),
    );
  return parts.length ? parts[parts.length - 1] : '';
}

/**
 * Store transversal de autorizacion (Flujo 2 - Comportamiento).
 *
 * Carga UNA vez el menu con acciones ya resueltas por Simappe
 * (POST /simappe-admin/api/v1/menu-with-actions/by-roles) e indexa, por segmento
 * de ruta de cada opcion, su mapa { actionCode: isGranted }.
 *
 * El front NO decide ni resuelve acciones (precedence, roles, colisiones ya vienen
 * resueltos del backend): solo consume el mapa. Cablear ese mapa a los botones es
 * responsabilidad aparte de cada vista/base.
 */
@Injectable({ providedIn: 'root' })
export class AuthorizationStore {
  private readonly http = inject(HttpService);

  private static readonly ENDPOINT = '/simappe-admin/api/v1/menu-with-actions/by-roles';

  /** Mapa segmento-de-opcion -> { actionCode: isGranted } (resuelto por Simappe). */
  private readonly actionsBySegment = signal<Map<string, Record<string, boolean>>>(new Map());

  /** Snapshot read-only para que los consumidores reaccionen a la carga asincrona. */
  readonly snapshot = this.actionsBySegment.asReadonly();

  /** true cuando la carga (exitosa o vacia) ya finalizo. */
  readonly loaded = signal(false);

  /**
   * Carga el menu con acciones y lo indexa por segmento de ruta. Fire-and-forget seguro:
   * nunca propaga error (ante fallo deja el mapa vacio y loaded=true).
   */
  load(): Observable<void> {
    const roleIds = this.roleIds();
    if (!roleIds.length) {
      this.actionsBySegment.set(new Map());
      this.loaded.set(true);
      return of(void 0);
    }

    return this.http.post<unknown>(AuthorizationStore.ENDPOINT, roleIds).pipe(
      map((response: any) => this.index(response?.response?.content ?? [])),
      tap((indexed) => {
        this.actionsBySegment.set(indexed);
        this.loaded.set(true);
      }),
      map(() => void 0),
      catchError(() => {
        this.loaded.set(true);
        return of(void 0);
      }),
    );
  }

  /** Acciones resueltas para la opcion activa segun la URL. {} si no hay match. */
  actionsForRoute(url: string | undefined): Record<string, boolean> {
    const seg = routeOptionSegment(url);
    return (seg && this.actionsBySegment().get(seg)) || {};
  }

  /** ¿La accion esta habilitada en la opcion de esa ruta? */
  can(url: string | undefined, actionCode: string): boolean {
    return this.actionsForRoute(url)[actionCode] === true;
  }

  private roleIds(): number[] {
    try {
      const raw = localStorage.getItem('user_data');
      if (!raw) return [];
      const userData = JSON.parse(raw);
      return (userData?.roles ?? [])
        .map((r: { id: number }) => r?.id)
        .filter((id: unknown): id is number => id != null);
    } catch {
      return [];
    }
  }

  private index(content: any[]): Map<string, Record<string, boolean>> {
    const result = new Map<string, Record<string, boolean>>();
    for (const role of content ?? []) {
      for (const app of role?.applications ?? []) {
        for (const mod of app?.modules ?? []) {
          for (const group of mod?.optionGroups ?? []) {
            for (const opt of group?.options ?? []) {
              const seg = routeOptionSegment(opt?.optionFullPath || opt?.optionPath);
              if (!seg) continue;
              const actions: Record<string, boolean> = opt?.actions ?? {};
              // Misma opcion puede repetirse bajo varios roles; Simappe ya resolvio
              // el valor por opcion, asi que el merge es idempotente.
              const prev = result.get(seg) ?? {};
              result.set(seg, { ...prev, ...actions });
            }
          }
        }
      }
    }
    return result;
  }
}
